import type { ReduxApplicationState } from 'src/redux/combinedReducer';
import { getCurrentTaxonomyDepth } from 'src/redux/productsQuery/helpers/getCurrentTaxonomyDepth';
import { getTaxonomyNavLevelOptions } from 'src/redux/productsQuery/helpers/getTaxonomyNavLevelOptions';
import type { Facet } from 'src/redux/productsQuery/types/Facet';
import type { TaxonomyAttribute } from 'src/redux/productsQuery/types/ProductsQueryState';

export interface TaxonomyContext {
  depth: number;
  facets: Facet[];
  /** True when the current level has no children to drill into (the deepest navigable level). */
  isDeepestLevel: boolean;
  surface?: string;
  taxonomyAttributeAliases: TaxonomyAttribute[];
  unsupported: boolean;
  valuesPerLevel: string[][];
}

const EMPTY_TAXONOMY_CONTEXT: TaxonomyContext = {
  depth: 0,
  facets: [],
  isDeepestLevel: true,
  taxonomyAttributeAliases: [],
  unsupported: false,
  valuesPerLevel: [],
};

/**
 * Resolves the taxonomy context for the current page, or an empty context when the route
 * is not a taxonomy page (no taxonomy attribute aliases configured).
 *
 * This is the single place that reads the `productsQuery` slice and computes the
 * current taxonomy state (depth, per-level selections, deepest-level flag). It
 * deliberately carries only state — no URLs, no display formatting. The
 * `storeToUI` taxonomy adapters (`adaptTaxonomyContextTo*`) turn this context
 * into the navigation, breadcrumb and parent-link view-models the UI renders.
 */
export const selectTaxonomyContext = (state: ReduxApplicationState): TaxonomyContext => {
  const { facets, surface, taxonomyAttributeAliases = [] } = state.productsQuery;

  if (taxonomyAttributeAliases.length === 0) {
    return EMPTY_TAXONOMY_CONTEXT;
  }

  const { depth, unsupported, valuesPerLevel } = getCurrentTaxonomyDepth(
    facets,
    taxonomyAttributeAliases,
  );
  // Resolve the next level's options once: an empty list means the current page
  // is the deepest navigable level.
  const childLevelOptions = getTaxonomyNavLevelOptions(facets, taxonomyAttributeAliases, depth);
  const isDeepestLevel = childLevelOptions.length === 0;

  return {
    depth,
    facets,
    isDeepestLevel,
    valuesPerLevel,
    surface,
    taxonomyAttributeAliases,
    unsupported,
  };
};
